import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  suppliersService,
  SupplierFilters,
  SupplierCreate,
  SupplierUpdate,
  BulkPriceUpdateRequest,
} from '@/services/suppliers';

export function useSuppliers(filters?: SupplierFilters, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['suppliers', filters],
    queryFn: () => suppliersService.getSuppliers(filters),
    enabled: options?.enabled ?? true,
  });
}

export function useSupplier(id: number) {
  return useQuery({
    queryKey: ['suppliers', id],
    queryFn: () => suppliersService.getSupplier(id),
    enabled: !!id,
  });
}

export function useSupplierProducts(id: number, onlyActive: boolean = true) {
  return useQuery({
    queryKey: ['suppliers', id, 'products', { onlyActive }],
    queryFn: () => suppliersService.getSupplierProducts(id, onlyActive),
    enabled: !!id,
  });
}

export function useCreateSupplier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: SupplierCreate) => suppliersService.createSupplier(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
    },
  });
}

export function useUpdateSupplier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: SupplierUpdate }) =>
      suppliersService.updateSupplier(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
    },
  });
}

export function useDeleteSupplier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => suppliersService.deleteSupplier(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
    },
  });
}

export function useBulkUpdateSupplierPrices() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      supplierId,
      data,
    }: {
      supplierId: number;
      data: Omit<BulkPriceUpdateRequest, 'supplier_id'>;
    }) => suppliersService.bulkUpdatePrices(supplierId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      queryClient.invalidateQueries({ queryKey: ['cost-history'] });
    },
  });
}

export function usePreviewBulkUpdateSupplierPrices() {
  return useMutation({
    mutationFn: ({
      supplierId,
      data,
    }: {
      supplierId: number;
      data: Omit<BulkPriceUpdateRequest, 'supplier_id'>;
    }) => suppliersService.previewBulkUpdatePrices(supplierId, data),
  });
}
